import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import HighscoreTimer from '@/components/HighscoreTimer';
import { Donation } from '@/hooks/useDonations';
import { useCurrency } from '@/contexts/CurrencyContext';
import { formatAmount } from '@/utils/formatAmount';

interface HeroSectionProps {
  donations: Donation[];
  onDonateClick: () => void;
  highestDonation: Donation | null;
}

const HeroSection = ({ donations, onDonateClick, highestDonation }: HeroSectionProps) => {
  const { t } = useTranslation();
  const { displayCurrency, convert } = useCurrency();
  const [displayedAmount, setDisplayedAmount] = useState(0);

  // Calculate total with currency conversion (with fallback for undefined)
  const totalAmount = (donations || []).reduce((sum, d) => {
    return sum + convert(Number(d.amount), d.currency);
  }, 0);

  // CountUp animation
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const increment = totalAmount / steps;
    let current = 0;
    
    const timer = setInterval(() => {
      current += increment;
      if (current >= totalAmount) {
        setDisplayedAmount(totalAmount);
        clearInterval(timer);
      } else {
        setDisplayedAmount(current);
      }
    }, duration / steps);

    return () => clearInterval(timer);
  }, [totalAmount]);

  return (
    <section className="min-h-[70vh] flex flex-col items-center justify-center px-4 pt-16 pb-12 sm:py-12">
      {/* Main Title */}
      <h1 className="text-4xl sm:text-6xl md:text-8xl lg:text-9xl font-bold tracking-wider terminal-glow glitch-hover text-center">
        DUMONGO.COM
      </h1>
      
      {/* Tagline */}
      <p className="mt-6 text-lg md:text-2xl text-muted-foreground text-center tracking-wide">
        {t('hero.tagline')}
      </p>

      {/* Total Counter */}
      <div className="mt-12 terminal-border p-6 md:p-8">
        <p className="text-sm md:text-base text-muted-foreground mb-2 text-center">
          {t('hero.totalWasted')}
        </p>
        <div className="text-4xl md:text-6xl lg:text-7xl font-bold terminal-glow text-center tabular-nums">
          {formatAmount(displayedAmount, displayCurrency)}
        </div>
        <p className="text-xs text-muted-foreground mt-2 text-center">
          {t('hero.burnedBy')}
        </p>
      </div>

      {/* Highscore Timer */}
      <HighscoreTimer highestDonation={highestDonation} />


      {/* CTA Button */}
      <Button
        onClick={onDonateClick}
        className="mt-10 text-lg md:text-xl px-8 py-6 h-auto terminal-border blink neon-box-shadow hover:bg-primary hover:text-primary-foreground transition-all"
        variant="outline"
        aria-label={t('hero.cta') + ' - ' + t('hero.tagline')}
      >
        {t('hero.cta')}
      </Button>

      {/* ASCII Art Decoration */}
      <pre className="mt-8 text-xs text-muted-foreground hidden md:block">
{`
  ╔══════════════════════════════════════╗
  ║   ${t('hero.moneyDoesntWork').padEnd(36)}║
  ║       ${t('hero.moneyRetires').padEnd(32)}║
  ╚══════════════════════════════════════╝
`}
      </pre>
    </section>
  );
};

export default HeroSection;
